import { useState, useMemo, useEffect } from "react";
import type { FormEvent, ChangeEvent } from "react";
import { money, fmt, calculate } from "../../lib/calculator";
// import logoImage from "../../assets/GenerateBi.png";
import "./GenerateBi.css";
import { getTokenExp } from "@/Services/req_res_decode_encode";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

type CalcResult = ReturnType<typeof calculate>;

function GenerateBi() {
  const [age, setAge] = useState(18);
  const [gender, setGender] = useState("Male");
  const [smoker, setSmoker] = useState("No");
  const [rop, setRop] = useState("NROP");
  const [payType, setPayType] = useState("LP");
  const [pt, setPt] = useState(5);
  const [ppt, setPpt] = useState(3);
  const [sa, setSa] = useState(1000000);
  const [paymentFrequency, setPaymentFrequency] = useState("Annual");
  const [overseas, setOverseas] = useState("No");
  const [diagnosis, setDiagnosis] = useState("No");
  const [staff, setStaff] = useState("No");
  const [digital, setDigital] = useState("No");

  const [result, setResult] = useState<CalcResult | null>(null);
  const [msg, setMsg] = useState<{ text: string; type: "err" | "ok" } | null>(null);
  const [openAlert, setOpenAlert] = useState(true);

  const navigate = useNavigate();
  const ptOptions = useMemo(() => {
    const min = rop === "ROP" ? 10 : 5;
    return Array.from({ length: 31 - min }, (_, i) => min + i);
  }, [rop]);

  const pptOptions = useMemo(() => {
    if (payType === "RP") return [pt];
    return Array.from({ length: Math.max(0, pt - 3) }, (_, i) => i + 3);
  }, [payType, pt]);

  useEffect(() => {
    const min = rop === "ROP" ? 10 : 5;
    if (pt < min) setPt(min);
  }, [rop, pt]);

  useEffect(() => {
    if (payType === "RP") {
      if (ppt !== pt) setPpt(pt);
    } else {
      if (ppt >= pt || ppt < 3) setPpt(Math.max(3, pt - 1));
    }
  }, [payType, pt, ppt]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const wrap = document.querySelector(".wrap");
    if (wrap) wrap.scrollTop = 0;
  }, []);

  const handleGenerate = (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      if (getTokenExp()) {
        localStorage.clear();
        toast.error("Your login is expired!");
        setTimeout(() => {
          window.location.href = import.meta.env.VITE_LOGINURL;
        }, 2000);
        return false;
      }
      const r = calculate({
        age,
        gender,
        smoker,
        rop,
        pay: payType,
        pt,
        ppt,
        sa,
        product: "Composite",
        paymentFrequency,
        overseas,
        diagnosis,
        staff,
        digital,
      });
      setResult(r);
      setMsg({
        text: "Report generated successfully in boxed sample format. Amounts rounded to nearest rupee.",
        type: "ok",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setMsg({ text: message, type: "err" });
      setResult(null);
    }
  };

  useEffect(() => {
    if (getTokenExp()) {
      setOpenAlert(false);
    } else {
      setOpenAlert(true);
    }
  }, []);

  const Alters = () => {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-xl p-6 shadow-xl w-[90%] max-w-md text-center"
            style={{ padding: 20 }}
          >
            <h2 className="text-red-600 text-xl font-bold mb-4"></h2>

            <p className="mb-6" style={{ marginTop: 10 }}>
              Your login is expired!
            </p>

            <button
              onClick={() => {
                localStorage.clear();
                setTimeout(() => {
                  window.location.href = import.meta.env.VITE_LOGINURL;
                }, 1000);
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
              style={{ width: 50, marginTop: 20 }}
            >
              Ok
            </button>
          </div>
        </div>
      </>
    );
  };

  if (!openAlert) {
    return <Alters />;
  }

  const handleReset = () => {
    setAge(18);
    setGender("Male");
    setSmoker("No");
    setRop("NROP");
    setPayType("LP");
    setPt(5);
    setPpt(3);
    setSa(1000000);
    setPaymentFrequency("Annual");
    setOverseas("No");
    setDiagnosis("No");
    setStaff("No");
    setDigital("No");
    setResult(null);
    setMsg(null);
  };

  const handlePrint = () => {
    try {
      if (getTokenExp()) {
        localStorage.clear();
        toast.error("Your login is expired!");
        setTimeout(() => {
          window.location.href = import.meta.env.VITE_LOGINURL;
        }, 2000);
        return false;
      }
      window.print();
    } catch {
      console.error("");
    }
  };

  const numChange = (setter: (v: number) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    setter(Number(e.target.value));
  const strChange = (setter: (v: string) => void) => (e: ChangeEvent<HTMLSelectElement>) =>
    setter(e.target.value);

  return (
    <div className="wrap">
      <div className="hero1 noPrint">
        <h1>Cancer Benefit Illustration Tool</h1>
        <p></p>
        {/* <img
          src={logoImage}
          alt=""
          className="logoPrintFix"
          style={{ position: "absolute", top: "10px", right: "20px", height: "55px", zIndex: 999 }}
        /> */}
      </div>
      <div
        className="noPrint"
        style={{ margin: "16px 0 12px 0", display: "flex", alignItems: "center" }}
      >
        <button
          className="topbar-change text-white bg-[#c8102e] border border-[#c8102e]  rounded-xl font-bold text-sm"
          style={{
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "25px",
            paddingRight: "30px",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          ← Back
        </button>
      </div>

      <div className="grid">
        <div className="card noPrint">
          <h2>Input Parameters</h2>
          <form className="form" onSubmit={handleGenerate}>
            <div className="row">
              <div className="field">
                <label>Age</label>
                <input
                  type="number"
                  min={18}
                  max={65}
                  value={age}
                  onChange={numChange(setAge)}
                  required
                />
                <small>Allowed: 18 to 65</small>
              </div>
              <div className="field">
                <label>Gender</label>
                <select value={gender} onChange={strChange(setGender)}>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>Smoker</label>
                <select value={smoker} onChange={strChange(setSmoker)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              <div className="field">
                <label>ROP / NROP</label>
                <select value={rop} onChange={strChange(setRop)}>
                  <option value="NROP">NROP</option>
                  <option value="ROP">ROP</option>
                </select>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>LP / RP</label>
                <select value={payType} onChange={strChange(setPayType)}>
                  <option value="LP">LP - Limited Pay</option>
                  <option value="RP">RP - Regular Pay</option>
                </select>
              </div>
              <div className="field">
                <label>Policy Term (PT)</label>
                <select value={pt} onChange={(e) => setPt(Number(e.target.value))}>
                  {ptOptions.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>Premium Payment Term (PPT)</label>
                <select value={ppt} onChange={(e) => setPpt(Number(e.target.value))}>
                  {pptOptions.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Cancer Sum Assured ({"\u20b9"})</label>
                <input
                  type="number"
                  min={100000}
                  max={5000000}
                  step={1000}
                  value={sa}
                  onChange={numChange(setSa)}
                  required
                />
                <small>
                  {"\u20b9"}1 lakh to {"\u20b9"}50 lakh
                </small>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>Payment Frequency</label>
                <select value={paymentFrequency} onChange={strChange(setPaymentFrequency)}>
                  <option value="Annual">Annual</option>
                  <option value="Semi Annual">Semi Annual</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Monthly">Monthly</option>
                </select>
                <small>Modal premium is calculated from annualised premium</small>
              </div>
              <div className="field">
                <label>Overseas Treatment Booster</label>
                <select value={overseas} onChange={strChange(setOverseas)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
                <small>Allowed only if Cancer SA is {"\u20b9"}10 lakh or above</small>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>Diagnostic Test Benefit</label>
                <select value={diagnosis} onChange={strChange(setDiagnosis)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label>Staff Discount (25%, Year 1 only)</label>
                <select value={staff} onChange={strChange(setStaff)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
              <div className="field">
                <label>Online Discount (10%, Year 1 only)</label>
                <select value={digital} onChange={strChange(setDigital)}>
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>
            </div>

            <div className="btns">
              <button type="submit" className="btn1 primary">
                Generate Report
              </button>
              <button type="button" className="btn1 secondary" onClick={handleReset}>
                Reset
              </button>
              <button type="button" className="btn1 success" onClick={handlePrint}>
                Print / Save PDF
              </button>
            </div>

            {msg && (
              <div id="msg" className={"msg " + msg.type} style={{ display: "block" }}>
                {msg.text}
              </div>
            )}
          </form>
        </div>
        <div className="card">
          <h2 className="noPrint">Final Output Report</h2>
          {result && (
            <div className="summary noPrint">
              <div className="metric">
                <b>Year 1 Modal Premium</b>
                <span>{money(result.firstYearModalPremium)}</span>
              </div>
              <div className="metric">
                <b>Renewal Modal Premium</b>
                <span>{money(result.renewalModalPremium)}</span>
              </div>
              <div className="metric">
                <b>Payment Frequency</b>
                <span>{result.paymentFrequency}</span>
              </div>
              <div className="metric">
                <b>Rate / 1000</b>
                <span>{Number(result.rate || 0).toFixed(4)}</span>
              </div>
            </div>
          )}
          <div className="report">
            {result ? (
              <Report result={result} />
            ) : (
              <div className="muted center">
                Please enter inputs and click <b>Generate Report</b>.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Report({ result }: { result: CalcResult }) {
  const dt = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const proposal = "Q" + Date.now();
  const discountLabel = result.isStaffDiscount
    ? "Staff Discount (25%)"
    : result.isOnlineDiscount
      ? "Online Discount (10%)"
      : "No Staff/Online Discount";

  return (
    <div className="bi">
      <h2 className="biTitle">BENEFIT ILLUSTRATION</h2>

      <div className="box">
        <div className="boxBody detailsGrid">
          <div>
            <b>Name of the Prospect/ Policyholder:</b> PLM Cancer
          </div>
          <div>
            <b>Gender:</b> {result.gender}
          </div>
          <div>
            <b>Age (yrs):</b> {result.age}
          </div>
          <div>
            <b>Name of the Life Assured:</b> PLM Cancer
          </div>
          <div>
            <b>Gender:</b> {result.gender}
          </div>
          <div>
            <b>Age (yrs):</b> {result.age}
          </div>
          <div>
            <b>Smoker:</b> {result.smoker}
          </div>
          <div>
            <b>Policy Term (years):</b> {result.pt}
          </div>
          <div>
            <b>Premium Payment Term (years):</b> {result.ppt}
          </div>
          <div>
            <b>Amount of Instalment Premium (Rs.):</b> {fmt(result.firstYearModalPremium)}
          </div>
          <div>
            <b>Mode of Payment of Premium:</b> {result.paymentFrequency}
          </div>
          <div>
            <b>Survival Period for CancerCare Benefit (in days):</b> 0
          </div>
          <div>
            <b>Proposal No:</b> {proposal}
          </div>
          <div>
            <b>Name of the Product:</b> Tata AIA Health Buddy Plus
          </div>
          <div>
            <b>Tag Line:</b> Non-Participating, Non-Linked Individual Health product
          </div>
          <div>
            <b>Unique Identification Number:</b> 110NxxxV01
          </div>
          <div>
            <b>GST Rate:</b> 0.00% for first year
          </div>
          <div>
            <b>0.00% second year onwards</b>
          </div>
        </div>
      </div>

      <div className="box">
        <div className="boxTitle">Policy Details</div>
        <div className="boxBody">
          <table className="tbl">
            <tbody>
              <tr>
                <th className="left">Policy Option</th>
                <th>Sum Assured (Rs.)</th>
              </tr>
              <tr>
                <td className="left">Accidental Death Benefit</td>
                <td>NA</td>
              </tr>
              <tr>
                <td className="left">Accidental Total and Permanent Disability</td>
                <td>NA</td>
              </tr>
              <tr>
                <td className="left">Term and Terminal Illness</td>
                <td>NA</td>
              </tr>
              <tr>
                <td className="left">Cancer</td>
                <td>{fmt(result.sa)}</td>
              </tr>
              <tr>
                <td className="left">Cardiac</td>
                <td>NA</td>
              </tr>
              <tr>
                <td className="left">OPD Plus</td>
                <td>NA</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="box">
        <div className="boxTitle">Rider Details</div>
        <div className="boxBody">
          <table className="tbl">
            <thead>
              <tr>
                <th>
                  Rider Name/UIN^
                  <br />
                  (Benefit Option/Benefit Payout Option)
                </th>
                <th>
                  Details of Life Insured/Partner under the Benefit Option
                  <br />
                  Name
                </th>
                <th>Age(yrs)</th>
                <th>Gender</th>
                <th>
                  Benefit Payout
                  <br />
                  Lumpsum (Rs.)
                </th>
                <th>Income p.a.(Rs.)</th>
                <th>Income Duration (yrs)</th>
                <th>Return of premium Option</th>
                <th>Benefit Option Term</th>
                <th>Premium Payment Term</th>
                <th>Sum Assured (Rs.)</th>
                <th>Premium p.a. without GST (Rs.)</th>
                <th>Year 1 Discount (Rs.)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cancer Care</td>
                <td>PLM Cancer</td>
                <td>{result.age}</td>
                <td>{result.gender}</td>
                <td>{fmt(result.sa)}</td>
                <td>0</td>
                <td>0</td>
                <td>{result.rop}</td>
                <td>{result.pt}</td>
                <td>{result.ppt}</td>
                <td>{fmt(result.sa)}</td>
                <td>{fmt(result.premiumBeforeDiscount)}</td>
                <td>{fmt(result.discountAmt)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="box">
        <div className="boxTitle">Premium Summary</div>
        <div className="boxBody">
          <table className="tbl">
            <tbody>
              <tr>
                <th></th>
                <th>Base Plan</th>
                <th>Riders</th>
                <th>Total</th>
              </tr>
              <tr>
                <td className="left">Payment Frequency</td>
                <td>{result.paymentFrequency}</td>
                <td>NA</td>
                <td>{result.paymentFrequency}</td>
              </tr>
              <tr>
                <td className="left">Annualised Premium without GST (Rs.)</td>
                <td>{fmt(result.premiumBeforeDiscount)}</td>
                <td>0</td>
                <td>{fmt(result.premiumBeforeDiscount)}</td>
              </tr>
              <tr>
                <td className="left">Modal Premium without GST (Rs.)</td>
                <td>{fmt(result.modalPremium)}</td>
                <td>0</td>
                <td>{fmt(result.modalPremium)}</td>
              </tr>
              <tr>
                <td className="left">
                  Modal Premium post applicable discount with First year GST (Rs.)
                </td>
                <td>{fmt(result.firstYearModalPremium)}</td>
                <td>0</td>
                <td>{fmt(result.firstYearModalPremium)}</td>
              </tr>
              <tr>
                <td className="left">Modal Premium with GST 2nd year onwards (Rs.)</td>
                <td>{fmt(result.renewalModalPremium)}</td>
                <td>0</td>
                <td>{fmt(result.renewalModalPremium)}</td>
              </tr>
              <tr>
                <td className="left" colSpan={4}>
                  Total Discount for first year premium for TATA AIA Health Buddy Includes -{" "}
                  {discountLabel}, (Annualised Discount Amount in Rupees): {fmt(result.discountAmt)}{" "}
                  | Modal Discount Amount in Rupees: {fmt(result.modalDiscountAmt)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="box">
        <div className="boxBody">
          <table className="tbl">
            <thead>
              <tr>
                <th>Policy Year</th>
                <th>Single/ Annualised Premium</th>
                <th>
                  Guaranteed
                  <br />
                  Maturity Benefit
                </th>
                <th>
                  Non - Guaranteed
                  <br />
                  AD Benefit
                </th>
                <th>TTI Benefit</th>
                <th>Cancer Benefit</th>
                <th>Cardiac Benefit</th>
                <th>ATPD Benefit</th>
                <th>Select CI Benefit</th>
                <th>Mega CI Benefit</th>
                <th>Guaranteed Surrender Value</th>
                <th>Special Surrender Value</th>
                <th>Surrender Value</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.year}>
                  <td>{r.year}</td>
                  <td>{fmt(r.premium)}</td>
                  <td>{fmt(r.maturity)}</td>
                  <td>{fmt(r.ad)}</td>
                  <td>{fmt(r.tti)}</td>
                  <td>{fmt(r.cancer)}</td>
                  <td>{fmt(r.cardiac)}</td>
                  <td>{fmt(r.atpd)}</td>
                  <td>{fmt(r.selectCI)}</td>
                  <td>{fmt(r.megaCI)}</td>
                  <td>{fmt(r.gsv)}</td>
                  <td>{fmt(r.ssv)}</td>
                  <td>{fmt(r.sv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="box">
        <div className="boxTitle">Notes:</div>
        <div className="boxBody smallNote">
          1. Annualized Premium shall be the premium payable in a year chosen by the policyholder,
          excluding the taxes, underwriting extra premiums, discounts, rider premiums, loading for
          modal premiums, if any.
          <br />
          2. This illustration is not an insurance contract.
          <br />
          3. Some benefits are guaranteed and some are non-guaranteed under this plan. The
          guaranteed and non-guaranteed benefits have been clearly marked 'Guaranteed' and 'Non
          Guaranteed' respectively in the illustration table above.
          <br />
          4. The applicable taxes, cesses, and levies, levied by the government from time to time,
          if any, shall be collected separately over and above the policy premiums.
          <br />
          5. Surrender Value shall be payable on completion of one policy year, provided one full
          year's premium is paid for non-single pay policies. Surrender value paid will be higher of
          Guaranteed Surrender Value and Special Surrender Value.
        </div>
      </div>

      <div className="box">
        <div className="boxBody smallNote">
          <b>Unique Reference Number - L&amp;C/Advt/2025/Jul/2599</b>
          <br />
          <b>
            Tata AIA Life Insurance Company Ltd. (IRDA of India Regn No. 110) (CIN:
            U66010MH2000PLC128403)
          </b>
          <br />
          Registered and Corporate Office: 14th Floor, Tower A, Peninsula Business Park, Senapati
          Bapat Marg, Lower Parel, Mumbai- 400013
        </div>
      </div>

      <div className="sign">
        <div>
          I, ______________________________ (name), have explained the premiums and benefits under
          the product fully to the prospect / policyholder.
          <br />
          <br />
          Date: {dt}
          <br />
          Place:
          <br />
          <br />
          Signature of Agent/ Intermediary/ Official
        </div>
        <div>
          I, PLM Cancer, having received the information with respect to the above, have understood
          the above statement before entering into the contract.
          <br />
          <br />
          Date: {dt}
          <br />
          <br />
          Signature of Prospect / Policyholder
        </div>
      </div>
    </div>
  );
}

export default GenerateBi;
